import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type B3DsCheckboxElement = HTMLElement & {
  selected?: boolean;
  disabled?: boolean;
  label?: string;
  error?: boolean;
};

@Directive({
  selector: 'b3-checkbox[formControlName], b3-checkbox[ngModel]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsCheckboxDirective),
      multi: true,
    },
  ],
})
export class B3DsCheckboxDirective implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = '';
  @Input() disabled = false;
  @Input() error = false;

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private value = false;
  private formDisabled = false;

  constructor(
    private readonly elementRef: ElementRef<B3DsCheckboxElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.syncElementState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['label'] || changes['disabled'] || changes['error']) {
      this.syncElementState();
    }
  }

  writeValue(value: boolean | null): void {
    this.value = !!value;
    this.syncElementState();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncElementState();
  }

  @HostListener('b3-change', ['$event'])
  onB3Change(event: Event): void {
    const customEvent = event as CustomEvent<{ selected?: boolean }>;
    this.value = !!customEvent.detail?.selected;
    this.syncElementState();
    this.onChange(this.value);
    this.onTouched();
  }

  private syncElementState(): void {
    const element = this.elementRef.nativeElement;
    element.selected = this.value;
    element.disabled = this.disabled || this.formDisabled;
    element.error = this.error;

    if (this.label) {
      element.label = this.label;
      this.renderer.setAttribute(element, 'label', this.label);
    }
  }
}
